import {
  Operation,
  QueryCombinerType,
  SimpleSearchFilter,
  SourceType,
} from 'module/api/filter/types';
import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { GroupSeparator } from 'module/types/filter-field/separators';
import { ApiGroupFilter } from 'module/ui/components/molecules/field-filter/simple-search-filter/simple-search-filter';
import { HttpClient } from 'api/http-client';
import { BlacklistedDomain } from 'queries/emails';
import { PaginatedEmailsResponse, Email, EmailThreadStatus } from './types';

export type { Email, PaginatedEmailsResponse };
export { EmailThreadStatus };

export type EmailProviderInfo = { gmail: string } | { outlook: string };

export enum EmailIntelligenceEnabledTo {
  SpecificTeamMembersOnly = 'SPECIFIC_TEAM_MEMBERS_ONLY',
  AllTeamMembers = 'ALL_TEAM_MEMBERS',
}

export interface EmailSettings {
  id?: string;
  emailIntelligenceEnabled: boolean;
  backSyncStartRange: string;
  backSyncStartDate: string;
  ftueCompleted: boolean;
  emailProviderInfo?: EmailProviderInfo;
  emailIntelligenceEnabledTo?: EmailIntelligenceEnabledTo;
}

export class EmailAPIClient extends HttpClient {
  async getBlacklistedDomains() {
    return this.get<BlacklistedDomain[]>(
      'scarlet/emails/settings/blacklisted-domains',
    );
  }

  async createBlacklistedDomain(email: string) {
    return this.post('scarlet/emails/settings/blacklisted-domains', { email });
  }

  async updateBlacklistedDomain({
    oldEmail,
    newEmail,
  }: {
    oldEmail: string;
    newEmail: string;
  }) {
    return this.put('scarlet/emails/settings/blacklisted-domains', {
      oldEmail,
      newEmail,
    });
  }

  async deleteBlacklistedDomain(email: string) {
    return this.delete('scarlet/emails/settings/blacklisted-domains', {
      data: { email },
    });
  }

  public async deleteEmailThread(emailThreadId: number): Promise<void> {
    return this.delete(`scarlet/emails/${emailThreadId}`);
  }

  public async getEmailThreadStatus(
    emailThreadId: number,
  ): Promise<{ status: EmailThreadStatus }> {
    return this.get<{ status: EmailThreadStatus }>(
      `scarlet/emails/${emailThreadId}/status`,
    );
  }

  async getEmailSettings() {
    return this.get<EmailSettings>('scarlet/emails/settings');
  }
  async updateEmailSettings(settings: EmailSettings) {
    return this.put<EmailSettings, EmailSettings>(
      'scarlet/emails/settings',
      settings,
    );
  }

  async createEmailSettings(settings: EmailSettings) {
    return this.post<EmailSettings, EmailSettings>(
      'scarlet/emails/settings',
      settings,
    );
  }

  public async getAllEmailsWithFilters(
    filter: GroupedFilter | undefined,
    pagination?: {
      offset?: number;
      limit?: number;
      companyId?: number;
    },
  ): Promise<PaginatedEmailsResponse> {
    const params: string[] = [];

    // Always scope the search to the company when provided. Expressed as a
    // native field filter (email*companyId.oneOf=...) — the same shape the
    // simple-search filter produces — so it composes with any view-derived
    // filter (native + custom fields) once saved views are wired up.
    if (pagination?.companyId != null) {
      params.push(
        `${SourceType.Email}${GroupSeparator}companyId.${Operation.OneOf}=${pagination.companyId}`,
      );
    }

    if (filter) {
      const filterQueryString = ApiGroupFilter.toQueryStringFormatForGroups(
        filter.groupCriteria,
      );

      if (filterQueryString) {
        params.push(filterQueryString, `match=${filter.type}`);
      }
    }

    // When only the companyId scope param was pushed (no filter criteria),
    // ensure match=all is present so the backend treats it as an AND query.
    if (params.length > 0 && !params.some((p) => p.startsWith('match='))) {
      params.push('match=all');
    }

    if (pagination?.offset !== undefined) {
      params.push(`offset=${pagination.offset}`);
    }
    if (pagination?.limit) {
      params.push(`limit=${pagination.limit}`);
    }

    params.push('sort=updatedAt.desc');

    return this.get<PaginatedEmailsResponse>(
      `/scarlet/emails/search?${params.join('&')}`,
    );
  }

  public async searchEmailsWithSimpleFilter(
    filter: SimpleSearchFilter,
    pagination?: {
      offset?: number;
      limit?: number;
      companyId?: number;
    },
  ): Promise<PaginatedEmailsResponse> {
    const queryParams: string[] = [];

    if (pagination?.offset !== undefined) {
      queryParams.push(`offset=${pagination.offset}`);
    }
    if (pagination?.limit) {
      queryParams.push(`limit=${pagination.limit}`);
    }
    const filterWithCompany: SimpleSearchFilter = {
      ...filter,
      nativeFields: [
        ...(filter.nativeFields ?? []),
        ...(pagination?.companyId != null
          ? [
              {
                sourceType: SourceType.Email,
                name: 'companyId',
                operation: Operation.OneOf,
                value: String(pagination.companyId),
              },
            ]
          : []),
      ],
      match: filter.match ?? QueryCombinerType.All,
    };

    queryParams.push('sort=updatedAt.desc');

    return this.post<PaginatedEmailsResponse, { filter: SimpleSearchFilter }>(
      `/scarlet/emails/search?${queryParams.join('&')}`,
      { filter: filterWithCompany },
    );
  }

  public async searchEmailsByCompanyId(
    companyId: number,
    options?: {
      offset?: number;
      limit?: number;
      searchTerm?: string;
    },
  ): Promise<PaginatedEmailsResponse> {
    const { offset = 0, limit = 25, searchTerm } = options || {};
    let url = `/email/search?email*companyId.oneOf=${companyId}&match=all&offset=${offset}&limit=${limit}`;

    if (searchTerm) {
      url += `&email*subject.contains=${encodeURIComponent(searchTerm)}`;
    }

    return this.get<PaginatedEmailsResponse>(url);
  }

  public async searchCompanyEmails(
    companyId: number,
    options?: {
      offset?: number;
      limit?: number;
      excludeEmailIds?: number[];
    },
  ): Promise<PaginatedEmailsResponse> {
    const { offset = 0, limit = 25, excludeEmailIds } = options || {};
    const queryParams: string[] = [
      `offset=${offset}`,
      `limit=${limit}`,
      'sort=createdAt.desc',
    ];

    if (excludeEmailIds && excludeEmailIds.length > 0) {
      queryParams.push(`emailThreadId.noneOf=${excludeEmailIds.join(',')}`);
    }

    const filter: SimpleSearchFilter = {
      match: QueryCombinerType.All,
      nativeFields: [
        {
          sourceType: SourceType.Email,
          name: 'companyId',
          operation: Operation.OneOf,
          value: String(companyId),
        },
      ],
    };

    return this.post<PaginatedEmailsResponse, { filter: SimpleSearchFilter }>(
      `/scarlet/emails/search?${queryParams.join('&')}`,
      { filter },
    );
  }
}

export const EmailAPI = new EmailAPIClient();
